import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import {
  Loader2,
  User,
  Building2,
  AlertCircle,
  Info,
  Clipboard,
  ClipboardCheck,
  ClipboardList,
} from 'lucide-react';
import { SessionType } from '@shared';
import { Badge, Button, Input } from '@design-system';
import { WaterlilyLogo } from '../components/WaterlilyLogo';
import CompanyLogoContainer from '../components/CompanyLogo';
import { cn } from '@/lib/utils';
import { selectSession } from '@/model/selectors';
import { ClientLinkAdvisorBlock } from '@client/components/ClientLinkAdvisorBlock';
import { useClientSignOut } from '@client/components/hooks/use-sign-out';
import { usePublicInviteFlow } from '@client/hooks/usePublicInviteFlow';
import { usePublicInviteSessionRedirect } from '@client/hooks/usePublicInviteSessionRedirect';
import {
  ReturningEmailChoice,
  ReturningSignIn,
  StartingNewAssessment,
} from '@client/components/PublicInviteReturningSteps';
import { PhoneNumberField } from '@client/components/PhoneNumberField';
import { useAdvisorPublicProfile } from '@client/hooks/useAdvisorPublicProfile';

export function AdvisorPublicInvite() {
  const { advisorId } = useParams<{ advisorId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isClerkSignedIn: isAuthenticated, isClerkLoading: authLoading } = useClerkClientAuth();
  const session = useSelector(selectSession);

  const {
    advisor,
    isLoading: isLoadingAdvisor,
    errorMessage: advisorError,
  } = useAdvisorPublicProfile(advisorId);

  const [linkCopied, setLinkCopied] = useState(false);

  const isAdvisorViewingLink =
    !authLoading &&
    isAuthenticated &&
    session.sessionType !== SessionType.unknown &&
    (session.sessionType === SessionType.advisor || session.sessionType === SessionType.wizard);

  const isEdjPilot = !!session.advisor?.featureFlags?.edjPilotAdvisor;
  // EDJ policy does not allow advisors to preview their reusable client link while signed in.
  const shouldBlockEdjAdvisor = isAdvisorViewingLink && isEdjPilot;
  // A role-bearing session without an advisor row cannot safely construct advisor navigation.
  const shouldBlockAdvisorWithoutRecord = isAdvisorViewingLink && !session.advisor?.advisorId;

  // The account email is the one the server checks when the new form is opened
  // (/onboarding-form compares it to the form's email), so it is the only email a signed-in
  // client can start an assessment with. A client session without one must sign out first.
  const isClientSession =
    !authLoading && isAuthenticated && session.sessionType === SessionType.client;
  const accountEmail = isClientSession ? (session.preLogin?.email ?? null) : null;
  const isSignedInClient = !!accountEmail;
  const mustSignOutToStart = isClientSession && !accountEmail;
  const isSessionResolving =
    !authLoading && isAuthenticated && session.sessionType === SessionType.unknown;

  const flow = usePublicInviteFlow({
    advisorId,
    signedInEmail: accountEmail,
    consumerSmsMfaRequired: !!advisor?.consumerSmsMfaRequired,
  });
  const signOut = useClientSignOut(`${location.pathname}${location.search}`);

  usePublicInviteSessionRedirect({
    isAuthLoading: authLoading,
    isAuthenticated,
    sessionType: session.sessionType,
  });

  if (shouldBlockEdjAdvisor || shouldBlockAdvisorWithoutRecord) {
    return <ClientLinkAdvisorBlock advisorId={session.advisor?.advisorId} />;
  }

  if (isLoadingAdvisor || authLoading || isSessionResolving) {
    return (
      <div className="from-primary-50 to-primary-100 flex min-h-screen items-center justify-center bg-gradient-to-br via-white">
        <div className="text-center">
          <Loader2 className="text-primary mx-auto h-12 w-12 animate-spin" />
          <p className="mt-4 text-gray-700">Loading...</p>
        </div>
      </div>
    );
  }

  if (advisorError) {
    return (
      <div className="from-primary-50 to-primary-100 flex min-h-screen items-center justify-center bg-gradient-to-br via-white px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl">
          <AlertCircle className="mx-auto h-16 w-16 text-red-700" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900">Invalid Link</h1>
          <p className="mt-2 text-gray-700">{advisorError}</p>
          <Button
            className="mt-6 w-full rounded-full"
            onClick={() => (window.location.href = '/')}
          >
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  const advisorFullName = advisor ? `${advisor.advisorFirstName} ${advisor.advisorLastName}` : '';
  const organizationDisplay = advisor?.organizationDisplayName || advisor?.organizationName;

  if (isAdvisorViewingLink) {
    const advisorId = session.advisor!.advisorId;
    const currentUrl = window.location.href;

    return (
      <div className="from-primary-50 to-primary-100 flex min-h-screen flex-col bg-gradient-to-br via-white">
        <header className="flex items-center justify-start px-8 py-6">
          <WaterlilyLogo className="h-10 w-auto" />
        </header>

        <main className="flex flex-1 items-center justify-center px-4 pb-12">
          <div className="w-full max-w-lg">
            <div className="rounded-2xl bg-white p-8 shadow-lg">
              <div className="flex flex-col items-center text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
                  <Info className="h-8 w-8 text-amber-600" />
                </div>

                <h1 className="mt-5 text-2xl font-semibold text-gray-900">
                  This Link is for Clients
                </h1>

                <p className="mt-3 text-gray-700">
                  This link is for clients to complete their intake form. Since you're logged in as
                  an advisor, you can't open it as a client.
                </p>
              </div>

              <div className="mt-8 space-y-3">
                <Button
                  className="w-full rounded-full py-6 text-base font-medium"
                  onClick={() =>
                    navigate(
                      `/advisors/${advisorId}/clients?inviteClient=true&inviteClientType=fill-form`,
                    )
                  }
                >
                  <ClipboardList className="mr-2 h-5 w-5" />
                  Fill Out Intake Form for a Client
                </Button>

                <Button
                  variant="outline"
                  className="w-full rounded-full py-6 text-base font-medium"
                  onClick={() => navigate(`/advisors/${advisorId}/clients`)}
                >
                  Go to Dashboard
                </Button>
              </div>

              <div className="mt-6 rounded-xl bg-gray-50 p-4">
                <p className="text-sm font-medium text-gray-900">Ways to share this link</p>
                <ul className="mt-2 space-y-1.5 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 block h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                    Send it to clients via text or email
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 block h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                    Embed it on your website or blog
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5 block h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400" />
                    Add it to your email templates or CRM workflows
                  </li>
                </ul>
              </div>

              <div className="mt-4 rounded-xl bg-blue-50 p-4">
                <p className="text-sm font-medium text-blue-900">
                  Want to see what your clients see?
                </p>
                <p className="mt-1 text-sm text-blue-800">
                  {isEdjPilot
                    ? 'This link will not work while you are logged in as an advisor. To test it as a client would, copy the link, sign out of your advisor account, then paste it in your browser.'
                    : 'Open this link in an incognito/private window to preview the client experience.'}
                </p>
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-white px-3 py-2">
                  <code className="flex-1 truncate text-xs text-gray-600">{currentUrl}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-blue-700 hover:text-blue-900"
                    onClick={() => {
                      navigator.clipboard.writeText(currentUrl);
                      setLinkCopied(true);
                      setTimeout(() => setLinkCopied(false), 2000);
                    }}
                  >
                    {linkCopied ? (
                      <ClipboardCheck className="h-4 w-4" />
                    ) : (
                      <Clipboard className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="py-4 text-center text-sm text-gray-700"></footer>
      </div>
    );
  }

  return (
    <div className="from-primary-50 to-primary-100 flex min-h-screen flex-col bg-gradient-to-br via-white">
      <header className="flex flex-wrap items-center justify-between gap-3 px-8 py-6">
        <CompanyLogoContainer
          organizationDisplayName={advisor?.organizationDisplayName}
          advisorLogoKey={advisor?.advisorLogoKey}
          brandingPreference={advisor?.whitelabelBrandingPreference}
          brandingMode={advisor?.brandingMode}
          waterlilyFallback="logo"
        />
        {isSignedInClient && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-700">
            <span>
              Signed in as <span className="font-semibold text-gray-900">{flow.email}</span>
            </span>
            <Button
              type="button"
              variant="link"
              className="text-primary h-auto p-0 text-sm font-semibold"
              onClick={signOut}
            >
              Sign out
            </Button>
          </div>
        )}
      </header>

      <main className="flex flex-1 items-center justify-center px-4 pb-12">
        <div className="w-full max-w-lg">
          <div className="mb-8 rounded-2xl bg-white p-6 shadow-lg">
            <div className="flex flex-col items-center text-center">
              <div className="bg-primary-100 flex h-20 w-20 items-center justify-center rounded-full">
                <User className="text-primary h-10 w-10" />
              </div>

              <p className="mt-4 text-sm font-medium text-gray-700">Your advisor</p>

              <h1 className="mt-1 text-2xl font-semibold text-gray-900">{advisorFullName}</h1>

              {organizationDisplay && (
                <div className="mt-2 flex items-center gap-2 text-gray-700">
                  <Building2 className="h-4 w-4" />
                  <span>{organizationDisplay}</span>
                </div>
              )}

              <p className="mt-4 max-w-xs text-gray-700">
                Begin your long-term care planning assessment with your advisor by entering your
                details below.
              </p>

              <p className="mt-3 max-w-xs text-xs text-gray-500">
                Your information is shared securely with your advisor.
              </p>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-8 shadow-lg">
            {flow.step === 'choice' && (
              <ReturningEmailChoice
                email={flow.email}
                fullName={flow.fullName}
                choice={flow.choice}
                isSubmitting={flow.isSubmitting}
                submitError={flow.submitError}
                onChoiceChange={flow.setChoice}
                onConfirm={flow.confirmChoice}
                onBack={flow.backToDetails}
              />
            )}

            {flow.step === 'signIn' && flow.signInUrl && (
              <ReturningSignIn
                email={flow.email}
                fullName={flow.fullName}
                choice={flow.choice}
                signInUrl={flow.signInUrl}
                onBack={flow.backToChoice}
              />
            )}

            {flow.step === 'starting' && (
              <StartingNewAssessment
                email={flow.email}
                fullName={flow.fullName}
                choice={flow.choice}
              />
            )}

            {mustSignOutToStart && (
              <div className="space-y-4 text-center">
                <h2 className="text-lg font-medium text-gray-900">Sign out to start an assessment</h2>
                <p className="text-sm text-gray-700">
                  You're signed in to an account without an email address. An assessment needs an
                  email, so please sign out and start again here.
                </p>
                <Button
                  className="w-full rounded-full py-6 text-base font-medium"
                  onClick={signOut}
                >
                  Sign out
                </Button>
              </div>
            )}

            {flow.step === 'details' && !mustSignOutToStart && (
              <>
                <div className="mb-6 space-y-2 text-center">
                  <h2 className="text-lg font-medium text-gray-900">
                    {isSignedInClient ? 'Who is this assessment for?' : 'Enter Your Information'}
                  </h2>
                  {isSignedInClient && (
                    <p className="text-sm text-gray-700">
                      It can be for you, or for someone else who uses this email.
                    </p>
                  )}
                </div>

                <form
                  onSubmit={flow.submitDetails}
                  className="space-y-4"
                >
                  <div>
                    <label
                      htmlFor="firstName"
                      className="mb-1.5 block text-sm font-medium text-gray-700"
                    >
                      First Name <span className="text-red-700">*</span>
                    </label>
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="Enter first name"
                      value={flow.formData.firstName}
                      onChange={e => flow.handleInputChange('firstName', e.target.value)}
                      className={cn(
                        flow.formErrors.firstName && 'border-red-500 focus-visible:ring-red-500',
                      )}
                      disabled={flow.isSubmitting}
                      autoComplete="given-name"
                    />
                    {flow.formErrors.firstName && (
                      <p className="mt-1 text-sm text-red-700">{flow.formErrors.firstName}</p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="lastName"
                      className="mb-1.5 block text-sm font-medium text-gray-700"
                    >
                      Last Name <span className="text-red-700">*</span>
                    </label>
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Enter last name"
                      value={flow.formData.lastName}
                      onChange={e => flow.handleInputChange('lastName', e.target.value)}
                      className={cn(
                        flow.formErrors.lastName && 'border-red-500 focus-visible:ring-red-500',
                      )}
                      disabled={flow.isSubmitting}
                      autoComplete="family-name"
                    />
                    {flow.formErrors.lastName && (
                      <p className="mt-1 text-sm text-red-700">{flow.formErrors.lastName}</p>
                    )}
                  </div>

                  {isSignedInClient ? (
                    <div>
                      <span className="mb-1.5 block text-sm font-medium text-gray-700">
                        Email Address
                      </span>
                      <div className="flex h-10 items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-900">
                        <span className="truncate">{flow.email}</span>
                        <Badge>Signed in</Badge>
                      </div>
                      <p className="mt-1 text-sm text-gray-700">
                        Not you?{' '}
                        <Button
                          type="button"
                          variant="link"
                          className="text-primary h-auto p-0 align-baseline text-sm font-semibold"
                          onClick={signOut}
                        >
                          Sign out
                        </Button>
                      </p>
                    </div>
                  ) : (
                    <div>
                      <label
                        htmlFor="email"
                        className="mb-1.5 block text-sm font-medium text-gray-700"
                      >
                        Email Address <span className="text-red-700">*</span>
                      </label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email address"
                        value={flow.formData.email}
                        onChange={e => flow.handleInputChange('email', e.target.value)}
                        className={cn(
                          flow.formErrors.email && 'border-red-500 focus-visible:ring-red-500',
                        )}
                        aria-invalid={!!flow.formErrors.email}
                        aria-describedby={flow.formErrors.email ? 'email-error' : undefined}
                        disabled={flow.isSubmitting}
                        autoComplete="email"
                      />
                      {flow.formErrors.email && (
                        <p
                          id="email-error"
                          className="mt-1 text-sm text-red-700"
                        >
                          {flow.formErrors.email}
                        </p>
                      )}
                    </div>
                  )}

                  {advisor?.consumerSmsMfaRequired && (
                    <PhoneNumberField
                      id="phoneNumber"
                      name="client-phone-manual-entry"
                      value={{
                        countryCode: flow.formData.phoneCountryCode,
                        phoneNumber: flow.formData.phoneNumber,
                      }}
                      onChange={{
                        countryCode: flow.changePhoneCountryCode,
                        phoneNumber: flow.changePhoneNumber,
                      }}
                      validation={{
                        invalid: Boolean(flow.formErrors.phoneNumber),
                        error:
                          (flow.isPhoneTouched || flow.formErrors.phoneNumber) &&
                          flow.formErrors.phoneNumber ? (
                            <p className="text-sm text-red-700">{flow.formErrors.phoneNumber}</p>
                          ) : undefined,
                        onBlur: flow.blurPhoneNumber,
                      }}
                      options={{ disabled: flow.isSubmitting, required: true, showHelper: false }}
                    />
                  )}

                  {flow.submitError && (
                    <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                      {flow.submitError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="mt-2 w-full rounded-full py-6 text-base font-medium"
                    disabled={flow.isSubmitting || !flow.isFormComplete}
                  >
                    {flow.isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Start Assessment'
                    )}
                  </Button>
                </form>

                <p className="mx-auto mt-4 max-w-[75%] text-left text-xs text-gray-700">
                  By continuing, you consent to receive communications related to your long-term
                  care planning assessment.
                </p>
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="py-4 text-center text-sm text-gray-700"></footer>
    </div>
  );
}
